import { useState, useRef, useEffect } from "react";
import { useEditorStore } from "@/store/editorStore";

// Renders one element (text/image/shape/button) inside an artboard.
// Handles selection, drag-move, and resize handles.
export default function ElementRenderer({ element, pageId, preview = false, scale = 1 }) {
  const selectedId = useEditorStore((s) => s.selectedElementId);
  const setSelectedElement = useEditorStore((s) => s.setSelectedElement);
  const updateElement = useEditorStore((s) => s.updateElement);
  const isSelected = !preview && selectedId === element.id;
  const ref = useRef(null);
  const dragRef = useRef(null);

  const startDrag = (e) => {
    if (preview) return;
    e.stopPropagation();
    setSelectedElement(element.id, pageId);
    if (element.type === "text") {
      // Don't start drag from inside contenteditable while editing — handled in inner element
    }
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = element.x;
    const origY = element.y;
    dragRef.current = { startX, startY, origX, origY };
    const onMove = (ev) => {
      const dx = (ev.clientX - dragRef.current.startX) / scale;
      const dy = (ev.clientY - dragRef.current.startY) / scale;
      updateElement(pageId, element.id, { x: origX + dx, y: origY + dy }, { skipHistory: true });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const startResize = (e, corner) => {
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const orig = { x: element.x, y: element.y, w: element.w, h: element.h };
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / scale;
      const dy = (ev.clientY - startY) / scale;
      let { x, y, w, h } = orig;
      if (corner.includes("e")) w = Math.max(20, orig.w + dx);
      if (corner.includes("s")) h = Math.max(20, orig.h + dy);
      if (corner.includes("w")) {
        w = Math.max(20, orig.w - dx);
        x = orig.x + dx;
      }
      if (corner.includes("n")) {
        h = Math.max(20, orig.h - dy);
        y = orig.y + dy;
      }
      updateElement(pageId, element.id, { x, y, w, h }, { skipHistory: true });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const baseStyle = {
    position: "absolute",
    left: element.x,
    top: element.y,
    width: element.w,
    height: element.h,
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    cursor: preview ? (element.href ? "pointer" : "default") : "move",
  };

  let inner = null;
  if (element.type === "text") {
    inner = (
      <div
        contentEditable={!preview && isSelected}
        suppressContentEditableWarning
        onBlur={(e) => {
          const text = e.currentTarget.innerText;
          if (text !== element.text) updateElement(pageId, element.id, { text });
        }}
        onMouseDown={(e) => {
          if (isSelected) e.stopPropagation();
        }}
        style={{
          width: "100%",
          height: "100%",
          padding: 4,
          outline: "none",
          fontSize: element.fontSize || 18,
          fontWeight: element.fontWeight || 400,
          color: element.color || "#111827",
          textAlign: element.align || "left",
          fontFamily: element.fontFamily || "inherit",
          lineHeight: 1.3,
          background: "transparent",
        }}
        data-testid={`element-text-${element.id}`}
      >
        {element.text}
      </div>
    );
  } else if (element.type === "image") {
    inner = (
      <img
        src={element.src}
        alt=""
        draggable={false}
        style={{ width: "100%", height: "100%", objectFit: element.fit || "cover", pointerEvents: "none" }}
        data-testid={`element-image-${element.id}`}
      />
    );
  } else if (element.type === "shape") {
    inner = (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: element.fill || "#002FA7",
          borderRadius: element.shape === "circle" ? "50%" : element.radius || 8,
          border: element.stroke ? `${element.strokeWidth || 1}px solid ${element.stroke}` : undefined,
        }}
        data-testid={`element-shape-${element.id}`}
      />
    );
  } else if (element.type === "button") {
    inner = (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 8,
          background: element.fill || "#002FA7",
          color: element.color || "#fff",
          fontWeight: 600,
        }}
        data-testid={`element-button-${element.id}`}
      >
        {element.text || "Button"}
      </div>
    );
  }

  const wrapper = (
    <div
      ref={ref}
      style={baseStyle}
      onMouseDown={startDrag}
      onClick={(e) => e.stopPropagation()}
      className={`${isSelected ? "ch-element-selected" : ""}`}
      data-element-id={element.id}
      data-testid={`element-wrapper-${element.id}`}
    >
      {inner}
      {isSelected && (
        <>
          <span className="ch-handle" style={{ left: -5, top: -5 }} onMouseDown={(e) => startResize(e, "nw")} />
          <span className="ch-handle" style={{ right: -5, top: -5 }} onMouseDown={(e) => startResize(e, "ne")} />
          <span className="ch-handle" style={{ left: -5, bottom: -5 }} onMouseDown={(e) => startResize(e, "sw")} />
          <span className="ch-handle" style={{ right: -5, bottom: -5 }} onMouseDown={(e) => startResize(e, "se")} />
        </>
      )}
    </div>
  );

  if (preview && element.href) {
    return (
      <a href={element.href} target="_blank" rel="noopener noreferrer" style={baseStyle}>
        <div style={{ width: "100%", height: "100%", position: "relative" }}>{inner}</div>
      </a>
    );
  }
  return wrapper;
}
